import React, { useEffect, useMemo, useState } from 'react';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import escapeStringRegexp from 'escape-string-regexp';
import Modal from 'components/Modal';
import { Button, Card, Col, Input, Row, Select, Space, Tag } from 'antd';
import globalMessages from 'translations/messages';

import { useQuery, useMutation } from 'hooks/apollo';
import { useDynamicList, useTextSelection } from 'ahooks';
import Table from 'components/Table';
import { DeleteFilled, EnterOutlined } from '@ant-design/icons';
import useFormatMessage from 'hooks/useFormatMessage';
import messages from './messages';
import './redact-pii-scan-personal-info.less';
import PII_SCAN_QUERY from './piiScan.gql';
import REDACT_MUTATION from '../RedactPersonalInfo/redactQuestionResponseMutation.gql';
import {
  getMaskCategoryOptions,
  maskCategoryForEntity,
  normalizePiiScanSettings,
} from 'shared/utils/piiScan';
import { regulationForEntityType } from './entityTypes';

const PII_SCAN_SETTINGS_QUERY = gql`
  query {
    settings(keys: ["piiScanSettings"])
  }
`;

const getReplaceRegex = (text) => new RegExp(`(^|\\W)${escapeStringRegexp(text)}(\\W|$)`, 'ig');

function findEntityOffsets(sourceText, entityText) {
  const trimmed = entityText.trim();
  if (!sourceText || !trimmed) {
    return { start: null, end: null };
  }

  const directIndex = sourceText.indexOf(trimmed);
  if (directIndex !== -1) {
    return { start: directIndex, end: directIndex + trimmed.length };
  }

  const match = getReplaceRegex(trimmed).exec(sourceText);
  if (!match) {
    return { start: null, end: null };
  }

  const fullMatch = match[0];
  const offsetWithinMatch = fullMatch.toLowerCase().indexOf(trimmed.toLowerCase());
  const start = match.index + Math.max(offsetWithinMatch, 0);
  return { start, end: start + trimmed.length };
}

function isSensitiveRegulation(regulation) {
  return regulation === 'Art. 9(1)';
}

function formatConfidence(confidence) {
  if (confidence == null || Number.isNaN(Number(confidence))) {
    return '—';
  }
  return `${Math.round(Number(confidence) * 100)}%`;
}

function buildTextSegments(text, entities, settings, anonymize = false) {
  if (!text) {
    return [];
  }

  const sorted = [...entities]
    .filter((e) => e.start != null && e.end != null && e.end > e.start)
    .sort((a, b) => a.start - b.start);

  const segments = [];
  let cursor = 0;

  sorted.forEach((entity) => {
    if (entity.start < cursor) {
      return;
    }
    if (entity.start > cursor) {
      segments.push({ kind: 'text', value: text.slice(cursor, entity.start) });
    }
    segments.push({
      kind: 'entity',
      value: anonymize ? entityPlaceholder(entity, settings) : text.slice(entity.start, entity.end),
      entity,
    });
    cursor = entity.end;
  });

  if (cursor < text.length) {
    segments.push({ kind: 'text', value: text.slice(cursor) });
  }

  if (segments.length === 0) {
    return [{ kind: 'text', value: text }];
  }

  return segments;
}

function TextPreview({ text, entities, settings, anonymize = false, className, id }) {
  const segments = useMemo(
    () => buildTextSegments(text, entities, settings, anonymize),
    [text, entities, settings, anonymize],
  );

  return (
    <div className={className} id={id}>
      {segments.map((segment, index) => {
        if (segment.kind === 'text') {
          return <span key={`text-${index}`}>{segment.value}</span>;
        }

        const { entity } = segment;
        const sensitive = isSensitiveRegulation(entity.regulation || regulationForEntityType(entity.type));
        if (anonymize) {
          return (
            <span
              key={`entity-${entity.id}-${index}`}
              className={`pii-scan-redaction__entity pii-scan-redaction__entity--${sensitive ? 'sensitive' : 'standard'}`}
            >
              {segment.value}
            </span>
          );
        }
        return (
          <span
            key={`entity-${entity.id}-${index}`}
            className={`pii-scan-redaction__entity pii-scan-redaction__entity--${sensitive ? 'sensitive' : 'standard'}`}
          >
            {segment.value}
          </span>
        );
      })}
    </div>
  );
}

function entityPlaceholder(entity, settings) {
  return `{{${maskCategoryForEntity(entity, settings)}}}`;
}

function getAnonymizedText(sourceText, entities, settings) {
  if (!sourceText) {
    return '';
  }

  const withOffsets = entities
    .filter((e) => e.start != null && e.end != null && e.end > e.start)
    .sort((a, b) => b.start - a.start);

  let text = sourceText;
  withOffsets.forEach((entity) => {
    text = `${text.slice(0, entity.start)}${entityPlaceholder(entity, settings)}${text.slice(entity.end)}`;
  });

  return entities
    .filter((e) => e.start == null || e.end == null)
    .reduce((acc, entity) => {
      const trimmed = entity.text.trim();
      if (!trimmed) {
        return acc;
      }
      return acc.replaceAll(getReplaceRegex(trimmed), `$1${entityPlaceholder(entity, settings)}$2`);
    }, text);
}

const RedactPiiScanPersonalInfo = ({ id, rowKey, onDone, getNextRowKey }) => {
  const effectiveRowKey = rowKey ?? `pii:${id}`;
  const $t = useFormatMessage();
  const { data, loading } = useQuery(PII_SCAN_QUERY, { variables: { id } });
  const { data: settingsData } = useQuery(PII_SCAN_SETTINGS_QUERY);
  const [redact, { loading: redactLoading }] = useMutation(REDACT_MUTATION);
  const [addEditText, setAddEditText] = useState('');
  const textSelection = useTextSelection(() => document.getElementById('response-text-pii'));
  const addEditInput = React.useRef(null);

  const entities = useDynamicList([]);

  const piiScan = data?.piiScan;
  const responseText = piiScan?.questionResponse?.text || '';
  const piiSettings = useMemo(
    () => normalizePiiScanSettings(settingsData?.settings?.piiScanSettings),
    [settingsData],
  );
  const maskCategoryOptions = useMemo(
    () => getMaskCategoryOptions(entities.list, piiSettings),
    [entities.list, piiSettings],
  );

  useEffect(() => {
    if (textSelection.text !== '') {
      setAddEditText(textSelection.text);
      addEditInput.current?.focus();
    }
  }, [textSelection.text]);

  useEffect(() => {
    if (!piiScan?.result) {
      return;
    }
    const detected = Array.isArray(piiScan.result.entities) ? piiScan.result.entities : [];
    entities.resetList(detected.map((entity, index) => ({
      id: entity.start ?? index,
      text: entity.entity != null ? String(entity.entity) : '',
      type: entity.type || 'PII',
      maskCategory: entity.maskCategory || null,
      regulation: entity.regulation || null,
      confidence: entity.confidence ?? null,
      start: entity.start ?? null,
      end: entity.end ?? null,
    })));
  }, [piiScan]);

  const onSubmit = async (next = false) => {
    const nextKeyAfterSave = next && getNextRowKey ? getNextRowKey(effectiveRowKey) : undefined;

    const variables = {
      blacklist: [],
      whitelist: [],
      language: null,
      text: getAnonymizedText(responseText, entities.list, piiSettings),
      questionResponseId: piiScan.questionResponse.id,
    };

    await redact({ variables });

    if (next) {
      onDone(true, nextKeyAfterSave, 'pii');
    } else {
      onDone(false);
    }
  };

  const onAddEntity = () => {
    const text = addEditText.trim();
    if (text?.length) {
      const offsets = findEntityOffsets(responseText, text);
      entities.push({
        id: `manual-${entities.list.length}`,
        text,
        type: 'PII',
        maskCategory: piiSettings.categoryMap.PII || 'pii',
        regulation: regulationForEntityType('PII'),
        confidence: null,
        ...offsets,
      });
    }
    setAddEditText('');
  };

  const footer = (
    <Space>
      <Button onClick={() => onDone()}>
        <FormattedMessage {...globalMessages.cancel} />
      </Button>
      <Button onClick={() => (getNextRowKey ? onDone(true, getNextRowKey(effectiveRowKey)) : onDone(true))}>
        <FormattedMessage {...globalMessages.next} />
      </Button>
      <Button type="primary" onClick={() => onSubmit(false)} loading={redactLoading}>
        <FormattedMessage {...globalMessages.save} />
      </Button>
      <Button type="primary" onClick={() => onSubmit(true)} loading={redactLoading}>
        <FormattedMessage {...messages.saveAndNext} />
      </Button>
    </Space>
  );

  const columns = [{
    dataIndex: 'text',
    title: $t(messages.entity),
    render: (text, record) => {
      const sensitive = isSensitiveRegulation(record.regulation || regulationForEntityType(record.type));
      return (
        <Tag
          className="pii-scan-redaction__entity-tag"
          color={sensitive ? 'orange' : 'blue'}
        >
          {text}
        </Tag>
      );
    },
  }, {
    dataIndex: 'maskCategory',
    title: $t(messages.type),
    width: 160,
    render: (_maskCategory, record, index) => (
      <Select
        className="pii-scan-redaction__type-select"
        showSearch
        optionFilterProp="label"
        options={maskCategoryOptions}
        value={maskCategoryForEntity(record, piiSettings)}
        onChange={(nextCategory) => entities.replace(index, {
          ...record,
          maskCategory: nextCategory,
        })}
      />
    ),
  }, {
    dataIndex: 'confidence',
    title: $t(messages.confidence),
    align: 'right',
    width: 110,
    render: (confidence) => formatConfidence(confidence),
  }];

  const actions = (_record, index) => [
    <Button danger key="delete" icon={<DeleteFilled />} onClick={() => { entities.remove(index); }} />,
  ];

  return (
    <Modal
      open
      fullWidth
      title={<FormattedMessage {...messages.formTitle} />}
      footer={footer}
      onCancel={() => onDone()}
      loading={loading}
    >
      {piiScan && (
        <div className="pii-scan-redaction">
          <div className="pii-scan-redaction__context">
            <p>
              <strong>
                {$t(globalMessages.customer)}
                :
              </strong>
              {' '}
              {piiScan.questionResponse.surveyResponse.survey.customer.name}
            </p>
            <p>
              <strong>
                {$t(globalMessages.question)}
                :
              </strong>
              {' '}
              <span dangerouslySetInnerHTML={{ __html: piiScan.questionResponse.question.title }} />
            </p>
          </div>

          <Row gutter={16} className="pii-scan-redaction__layout">
            <Col span={14} className="pii-scan-redaction__main">
              <div className="pii-scan-redaction__section">
                <div className="pii-scan-redaction__section-header">
                  <FormattedMessage {...messages.response} />
                </div>
                <TextPreview
                  text={responseText}
                  entities={entities.list}
                  settings={piiSettings}
                  className="pii-scan-redaction__text-box pii-scan-redaction__text-box--selectable"
                  id="response-text-pii"
                />
              </div>

              <div className="pii-scan-redaction__section">
                <div className="pii-scan-redaction__section-header">
                  <FormattedMessage {...messages.results} />
                </div>
                <TextPreview
                  text={responseText}
                  entities={entities.list}
                  settings={piiSettings}
                  anonymize
                  className="pii-scan-redaction__text-box"
                />
              </div>
            </Col>

            <Col span={10} className="pii-scan-redaction__sidebar">
              <Card
                className="pii-scan-redaction__card pii-scan-redaction__entities-card"
                size="small"
                title={<FormattedMessage {...messages.detectedEntities} />}
              >
                <Table
                  className="break-word pii-scan-redaction__entities-table pii-scan-redaction__entities-table--actions"
                  dataSource={entities.list}
                  columns={columns}
                  actions={actions}
                  pagination={false}
                  resizable={false}
                  size="small"
                />
                <Input
                  className="pii-scan-redaction__add-entity"
                  placeholder={$t(messages.addEntity)}
                  value={addEditText}
                  onChange={(e) => setAddEditText(e.target.value)}
                  onPressEnter={onAddEntity}
                  suffix={<EnterOutlined onClick={onAddEntity} style={{ cursor: 'pointer' }} />}
                  ref={addEditInput}
                />
              </Card>
            </Col>
          </Row>
        </div>
      )}
    </Modal>
  );
};

export default RedactPiiScanPersonalInfo;
